// marketShareUtils.js
import { SCHOOL_CATEGORIES } from './schoolCategorization';
import {
  BASELINE_STATUS,
  resolveBaselineStatus,
} from '../constants/ringMembership';
import { 
  calculateEnrollment,
  filterAgeData,
  calculateTotalChildren,
  getSchoolTimeframe,
  RACE_CATEGORIES
} from './schoolUtils';

/**
 * Core types of market share calculations
 */
export const MARKET_SHARE_TYPES = {
  ENROLLMENT: 'enrollment', // Share of total enrollment across schools
  POPULATION: 'population', // Share of total school-age population
  DEMOGRAPHIC: 'demographic' // Share by racial/ethnic group
};

/**
 * Check if a school should have historical comparison data
 * @param {string} category - The school's category
 * @returns {boolean}
 */
export const shouldHaveHistoricalData = (category) => {
    return ![
      SCHOOL_CATEGORIES.NEW,
      SCHOOL_CATEGORIES.NEWER_INCREASING,
      SCHOOL_CATEGORIES.NEWER_DECREASING,
      SCHOOL_CATEGORIES.NEWER_STABLE
    ].includes(category);
  };

/**
 * Resolve the baseline state for a market share calculation.
 *
 * Category alone is not enough: it only sees enrollment history, so a school that has
 * relocated, or whose baseline drive-time area was never processed, reads as CONTINUOUS
 * and used to be handed an empty baseline universe. Dividing the school's own enrollment
 * by an empty area total reported a 100% baseline share and a collapse of 70-90 points
 * for schools that had in fact grown.
 */
const baselineStatusFor = (schoolData, nearbySchools, selectedDriveTime) =>
  resolveBaselineStatus(
    nearbySchools,
    selectedDriveTime,
    !shouldHaveHistoricalData(schoolData?.category)
  );

/**
 * Calculate population-based market share
 * @param {Object} params Configuration object
 * @param {Object} params.schoolData School enrollment data
 * @param {Array} params.selectedGrades Array of selected grade levels
 * @param {Object} params.esriData ESRI demographic data
 * @param {number} params.selectedDriveTime Drive time in minutes
 * @param {string} [params.timepoint='current'] Time period to calculate for
 * @returns {number} Market share percentage
 */
const calculatePopulationShare = ({
  schoolData,
  nearbySchools,
  selectedGrades,
  esriData,
  selectedDriveTime,
  timepoint = 'current'
}) => {
  // A baseline share pairs the school's own baseline enrollment with the population in
  // its area. For a school that has moved those two describe different places, so there
  // is no share to report rather than a misleading one.
  if (timepoint === 'comparison'
      && baselineStatusFor(schoolData, nearbySchools, selectedDriveTime) !== BASELINE_STATUS.ELIGIBLE) {
    return null;
  }

  // Early returns check
  if (!schoolData?.enrollment_by_grade || !esriData?.drive_times?.[selectedDriveTime]?.ages['4_17']) {
    return 0;
  }

  // Get filtered age data for selected grades
  const ageData = filterAgeData(
    esriData.drive_times[selectedDriveTime].ages['4_17'],
    selectedGrades
  );

  // Get population data for right year 
  const yearData = timepoint === 'current' ? ageData.current : ageData['2020'];
  
  // Calculate population total
  const totalPopulation = calculateTotalChildren(yearData);

  // Calculate school enrollment
  const schoolEnrollment = calculateEnrollment(
    schoolData.enrollment_by_grade[timepoint] || {},
    selectedGrades
  );

  return totalPopulation > 0 ? (schoolEnrollment / totalPopulation) * 100 : 0;
};

/**
 * Calculate enrollment-based market share
 * @param {Object} params Configuration object
 * @param {Object} params.school School data
 * @param {Object} params.schoolData School enrollment data
 * @param {Object} params.nearbySchools Nearby schools data
 * @param {Array} params.schoolsEnrollmentData All schools' enrollment data
 * @param {Array} params.selectedGrades Selected grade levels
 * @param {number} params.selectedDriveTime Drive time in minutes
 * @param {Array} [params.governanceFilter=['Charter','District','Private']] Governance filter array
 * @param {string} [params.timepoint='current'] Time period to calculate for
 * @returns {number} Market share percentage
 */
const calculateEnrollmentShare = ({
  school,
  schoolData,
  nearbySchools,
  schoolsEnrollmentData,
  selectedGrades,
  selectedDriveTime,
  governanceFilter = ['Charter', 'District', 'Private'],
  timepoint = 'current'
}) => {
  // No baseline area means no baseline share. Returning 0 here (or falling through to an
  // empty universe) is what produced the 100% baseline share.
  if (timepoint === 'comparison'
      && baselineStatusFor(schoolData, nearbySchools, selectedDriveTime) !== BASELINE_STATUS.ELIGIBLE) {
    return null;
  }

  // Get appropriate universe of schools
  const relevantSchools = nearbySchools?.[timepoint]?.[selectedDriveTime] || [];

  // An eligible school always has a populated baseline ring, so an empty universe here
  // means the data did not arrive. Reporting the school as its own whole market would be
  // wrong in either case.
  if (timepoint === 'comparison' && relevantSchools.length === 0) {
    return null;
  }
  
  // Filter by governance if needed (governanceFilter is now an array)
  const filteredSchools = (!governanceFilter || governanceFilter.length === 0 || governanceFilter.length === 3)
    ? relevantSchools
    : relevantSchools.filter(s => {
        const isCharter = s.charter === 'Yes' || s.is_charter === true;
        const isPrivate = s.is_private === true;
        
        let matches = false;
        
        if (governanceFilter.includes('Charter')) {
          if (isCharter && !isPrivate) matches = true;
        }
        if (governanceFilter.includes('District')) {
          if (!isCharter && !isPrivate) matches = true;
        }
        if (governanceFilter.includes('Private')) {
          if (isPrivate) matches = true;
        }
        
        return matches;
      });

  // Calculate school's enrollment
  const schoolEnrollment = calculateEnrollment(
    schoolData?.enrollment_by_grade?.[timepoint] || {},
    selectedGrades
  );

  // Calculate total area enrollment
  const areaEnrollment = filteredSchools.reduce((total, nearbySchool) => {
    const nearbyData = schoolsEnrollmentData.find(d => d.uuid === nearbySchool.uuid);
    return total + calculateEnrollment(
      nearbyData?.enrollment_by_grade?.[timepoint] || {},
      selectedGrades
    );
  }, 0);

  // Include school's enrollment in total if it matches governance filter
  const isSchoolCharter = school.charter === 'Yes' || school.is_charter === true;
  const isSchoolPrivate = school.is_private === true;
  const shouldIncludeSchool = (!governanceFilter || governanceFilter.length === 0 || governanceFilter.length === 3) ||
    (governanceFilter.includes('Charter') && isSchoolCharter && !isSchoolPrivate) ||
    (governanceFilter.includes('District') && !isSchoolCharter && !isSchoolPrivate) ||
    (governanceFilter.includes('Private') && isSchoolPrivate);

  const totalEnrollment = shouldIncludeSchool ? 
    areaEnrollment + schoolEnrollment : 
    areaEnrollment;

  return totalEnrollment > 0 ? (schoolEnrollment / totalEnrollment) * 100 : 0;
};

/**
 * Calculate comprehensive market share statistics
 * @param {Object} params Configuration object
 * @param {string} [params.type=MARKET_SHARE_TYPES.ENROLLMENT] Type of market share calculation
 * @param {Object} params.school School data
 * @param {Object} params.schoolData School enrollment data
 * @returns {Object} Market share statistics
 */
export const calculateMarketShareStats = ({
  type = MARKET_SHARE_TYPES.ENROLLMENT,
  school,
  schoolData,
  selectedGrades,
  esriData,
  selectedDriveTime,
  ...params
}) => {
  // Choose appropriate calculation method
  const calculateShare = type === MARKET_SHARE_TYPES.POPULATION ?
    calculatePopulationShare :
    calculateEnrollmentShare;

  // Calculate current share
  const currentShare = calculateShare({
    school,
    schoolData,
    selectedGrades,    // Add these missing parameters
    esriData,
    selectedDriveTime,
    ...params,
    timepoint: 'current'
  });

  const baselineStatus = baselineStatusFor(schoolData, params.nearbySchools, selectedDriveTime);
  const hasValidHistory = baselineStatus === BASELINE_STATUS.ELIGIBLE;

  // Calculate past share
  const pastShare = hasValidHistory ?
    calculateShare({
      school,
      schoolData,
      selectedGrades,    // Add these missing parameters
      esriData,
      selectedDriveTime,
      ...params,
      timepoint: 'comparison'
    }) : null;

  // A share of null means the baseline is unavailable, so there is no change to report.
  // Consumers must treat null as "not applicable" rather than as zero.
  const hasPastShare = pastShare !== null && pastShare !== undefined;

  // Return stats
  return {
    current: currentShare,
    past: hasPastShare ? pastShare : null,
    change: hasPastShare ? currentShare - pastShare : null,
    hasValidHistory: hasValidHistory && hasPastShare,
    baselineStatus,
    timeframe: getSchoolTimeframe(schoolData),
    metadata: {
      schoolCategory: schoolData?.category,
      isNewOrNewer: !shouldHaveHistoricalData(schoolData?.category),
      baselineStatus
    }
  };
};

/**
 * Calculate demographic market share statistics
 * @param {Object} params Configuration object
 * @param {Object} params.school School data
 * @param {Object} params.schoolData School enrollment data
 * @param {Object} params.nearbySchools Nearby schools data
 * @param {Array} params.schoolsEnrollmentData All schools' enrollment data
 * @param {Array} params.selectedGrades Selected grade levels
 * @param {number} params.selectedDriveTime Drive time in minutes
 * @param {string} [params.timepoint='current'] Time period to calculate for
 * @returns {Array} Array of demographic share statistics
 */
export const calculateDemographicShares = ({
  school,
  schoolData,
  nearbySchools,
  schoolsEnrollmentData,
  selectedGrades,
  selectedDriveTime,
  timepoint = 'current'
}) => {
  const baselineStatus = baselineStatusFor(schoolData, nearbySchools, selectedDriveTime);
  const hasBaseline = baselineStatus === BASELINE_STATUS.ELIGIBLE;

  // Without a comparable baseline area there is nothing to report for the past timepoint.
  if (timepoint === 'comparison' && !hasBaseline) {
    return Object.entries(RACE_CATEGORIES).map(([field, label]) => ({
      category: label,
      field,
      share: 0,
      schoolCount: 0,
      areaCount: 0,
      totalCount: 0,
      hasHistory: false,
      baselineStatus
    }));
  }

  // Get appropriate universe of schools
  const relevantSchools = nearbySchools?.[timepoint]?.[selectedDriveTime] || [];

  return Object.entries(RACE_CATEGORIES).map(([field, label]) => {
    // Calculate school's demographic count
    const schoolCount = selectedGrades.reduce((total, grade) => {
      return total + (schoolData?.race_data?.[timepoint]?.[grade]?.[field] || 0);
    }, 0);

    // Calculate area demographic count (excluding selected school)
    const areaCount = relevantSchools
      .filter(s => s.uuid !== school.uuid)
      .reduce((total, nearbySchool) => {
        const nearbyData = schoolsEnrollmentData.find(d => d.uuid === nearbySchool.uuid);
        return total + selectedGrades.reduce((gradeTotal, grade) => {
          return gradeTotal + (nearbyData?.race_data?.[timepoint]?.[grade]?.[field] || 0);
        }, 0);
      }, 0);

    const totalCount = schoolCount + areaCount;
    const share = totalCount > 0 ? (schoolCount / totalCount) * 100 : 0;

    return {
      category: label,
      field,
      share,
      schoolCount,
      areaCount,
      totalCount,
      hasHistory: hasBaseline,
      baselineStatus
    };
  });
};

/**
 * Calculate comprehensive demographic market share statistics including trends
 * @param {Object} params Parameters for demographic calculations
 * @returns {Array} Array of demographic statistics with historical comparison
 */
export const calculateDemographicMarketShares = (params) => {
  const currentShares = calculateDemographicShares({
    ...params,
    timepoint: 'current'
  });

  const pastShares = calculateDemographicShares({
    ...params,
    timepoint: 'comparison'
  });

  return currentShares.map(current => {
    const past = pastShares.find(p => p.field === current.field);
    return {
      ...current,
      pastShare: past?.share || 0,
      pastSchoolCount: past?.schoolCount || 0,
      pastAreaCount: past?.areaCount || 0,
      pastTotalCount: past?.totalCount || 0,
      shareChange: past ? current.share - past.share : 0,
      hasHistory: Boolean(past?.totalCount && current.hasHistory)
    };
  });
};